import React, { useEffect, useState } from 'react';

import {
  Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { downloadActionPlanEvidence } from '../../../api/climateActionPlanApi';
import LabeledField from '../../../shared/components/LabeledField';
import AppDialog from '../../../shared/components/AppDialog';
import { SCROLLABLE_SELECT_MENU_PROPS } from '../../../shared/components/selectMenuProps';
import { formatTonne } from '../../../shared/utils/number';
import EvidenceUploadList from './EvidenceUploadList';
import { RESULT_STATUS, RESULT_STATUS_OPTIONS, resultStatusLabelKey } from '../model/actionStatusModel';
import {
  normalizeActionPlanResult, normalizeActionPlanResultInputOptions, serializeActionPlanResult,
} from './actionPlanModel';

const emptyResult = fiscalYear => ({
  fiscalYear,
  inputMode: 'DIRECT_TCO2E',
  activityValue: '',
  reportedTco2e: '',
  basisNote: '',
  status: RESULT_STATUS.DRAFT,
  evidenceFiles: [],
});

const resultForYear = (plan, fiscalYear) => (
  (plan?.annualResults || []).find(result => result.fiscalYear === fiscalYear) || null
);

/** Record or edit the annual result and evidence for one action plan in one fiscal year. */
export default function ActionPlanResultDialog({
  canMutate, fiscalYear, onClose, onDeleteEvidence, onError, onLoadResultInputOptions,
  onRefresh, onSaveResult, onUploadEvidence, open, plan, t,
}) {
  const [draft, setDraft] = useState(null);
  const [resultOptions, setResultOptions] = useState({});
  const [removedEvidenceIds, setRemovedEvidenceIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open || !plan) return;
    const existing = resultForYear(plan, fiscalYear);
    const normalized = existing ? normalizeActionPlanResult(existing) : emptyResult(fiscalYear);
    setDraft({ ...normalized, evidenceFiles: normalized.evidenceFiles || [] });
    setResultOptions({});
    setRemovedEvidenceIds([]);
    setSaving(false);
    setSaveError(null);
    if (plan.id && onLoadResultInputOptions) {
      onLoadResultInputOptions(plan.id, fiscalYear)
        .then(options => setResultOptions(options))
        .catch(() => setResultOptions(normalizeActionPlanResultInputOptions({})));
    }
  }, [open, plan, fiscalYear, onLoadResultInputOptions]);

  const activityBasedAvailable = Boolean(resultOptions.activityBasedAvailable);

  const computedTco2e = (() => {
    if (!activityBasedAvailable || !draft) return null;
    const activityValue = draft.activityValue;
    if (activityValue === '' || activityValue == null) return null;
    const factorValue = Number(resultOptions.factorValue);
    const activity = Number(activityValue);
    if (!Number.isFinite(factorValue) || factorValue <= 0 || !Number.isFinite(activity)) return null;
    return activity * factorValue;
  })();

  const conversionNote = (() => {
    if (!activityBasedAvailable) return '';
    const parts = [];
    if (resultOptions.factorValue != null && resultOptions.factorUnit) {
      parts.push(`EF ${resultOptions.factorValue} ${resultOptions.factorUnit}`);
    }
    if (resultOptions.businessName) parts.push(resultOptions.businessName);
    if (resultOptions.effectiveYear != null) parts.push(`FY${resultOptions.effectiveYear}`);
    return parts.join(' · ');
  })();

  const updateField = (field, value) => {
    if (field === 'inputMode') {
      setDraft(current => {
        const next = { ...current, inputMode: value };
        if (value === 'DIRECT_TCO2E'
          && (current.reportedTco2e === '' || current.reportedTco2e == null)
          && computedTco2e != null) {
          next.reportedTco2e = String(computedTco2e);
        }
        if (value === 'ACTIVITY_BASED' && activityBasedAvailable
          && (current.activityValue === '' || current.activityValue == null)) {
          const factorValue = Number(resultOptions.factorValue);
          const reported = Number(current.reportedTco2e);
          if (Number.isFinite(factorValue) && factorValue > 0 && Number.isFinite(reported)) {
            next.activityValue = String(reported / factorValue);
          }
        }
        return next;
      });
      return;
    }
    setDraft(current => ({ ...current, [field]: value }));
  };

  const addEvidence = record => {
    setDraft(current => ({ ...current, evidenceFiles: [...(current.evidenceFiles || []), record] }));
  };

  const removeEvidence = evidenceId => {
    const record = (draft?.evidenceFiles || []).find(file => file.id === evidenceId);
    if (record && !record.file && record.id && !String(record.id).startsWith('evidence-')) {
      setRemovedEvidenceIds(current => [...current, record.id]);
    }
    setDraft(current => ({
      ...current,
      evidenceFiles: (current.evidenceFiles || []).filter(file => file.id !== evidenceId),
    }));
  };

  const downloadEvidence = async evidence => {
    if (!evidence?.id) return;
    try {
      const blob = await downloadActionPlanEvidence(evidence.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = evidence.name || 'evidence';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      onError?.(error);
    }
  };

  const handleSave = async () => {
    if (!plan?.id || !draft || !onSaveResult) return;
    const missingRequiredField = draft.inputMode === 'ACTIVITY_BASED'
      ? draft.activityValue === '' || draft.activityValue == null
      : draft.reportedTco2e === '' || draft.reportedTco2e == null;
    if (missingRequiredField || !draft.status) {
      setSaveError(t('climateAction.plan.dialog.requiredFields', {
        defaultValue: 'Complete all required fields before saving.',
      }));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const savedResult = await onSaveResult(
        plan.id,
        fiscalYear,
        serializeActionPlanResult(draft),
        draft.status,
      );
      const resultId = savedResult?.id;
      const newFiles = (draft.evidenceFiles || [])
        .filter(record => record.file)
        .map(record => record.file);
      if (resultId && newFiles.length > 0 && onUploadEvidence) {
        await onUploadEvidence(resultId, newFiles);
      }
      for (const evidenceId of removedEvidenceIds) {
        if (onDeleteEvidence) await onDeleteEvidence(evidenceId);
      }
      onRefresh?.();
      onClose();
    } catch (error) {
      setSaveError(t('common.feedback.save.failed', { defaultValue: 'Cannot save the annual result. Check the entered data.' }));
      onError?.(error);
    } finally {
      setSaving(false);
    }
  };

  if (!plan) return null;

  return (
    <AppDialog
      fullWidth
      maxWidth="md"
      onClose={saving ? undefined : onClose}
      open={open}
    >
      <DialogTitle sx={{ pr: 7 }}>
        {t('climateAction.plan.dialog.resultEditorTitle', { year: fiscalYear })}
        <IconButton aria-label={t('common.close')} disabled={saving} onClick={onClose} sx={{ position: 'absolute', right: 18, top: 18 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {draft && (
          <Stack spacing={1.5}>
            <RadioGroup row onChange={event => updateField('inputMode', event.target.value)} value={draft.inputMode}>
              <Stack direction="row" alignItems="center">
                <Radio disabled={!canMutate} size="small" value="DIRECT_TCO2E" />
                <Typography sx={{ fontSize: 13 }}>{t('climateAction.plan.dialog.directTco2e')}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Radio disabled={!canMutate} size="small" value="ACTIVITY_BASED" />
                <Typography sx={{ fontSize: 13 }}>{t('climateAction.plan.dialog.activityBased')}</Typography>
              </Stack>
            </RadioGroup>
            {draft.inputMode === 'ACTIVITY_BASED' ? (
              <Stack spacing={0.75}>
                <LabeledField
                  helper={!activityBasedAvailable ? t('climateAction.plan.dialog.activityUnavailableHint') : undefined}
                  label={`${t('climateAction.plan.dialog.activityValue')} (${resultOptions.activityUnit || ''})`}
                  required
                >
                  <TextField
                    disabled={!canMutate || !activityBasedAvailable}
                    fullWidth
                    onChange={event => updateField('activityValue', event.target.value)}
                    size="small"
                    type="number"
                    value={draft.activityValue}
                  />
                </LabeledField>
                {computedTco2e != null && (
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    = {formatTonne(computedTco2e, { withUnit: true })}
                  </Typography>
                )}
                {conversionNote && (
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {conversionNote}
                  </Typography>
                )}
              </Stack>
            ) : (
              <LabeledField label={t('climateAction.plan.dialog.reportedTco2e')} required>
                <TextField
                  disabled={!canMutate}
                  fullWidth
                  onChange={event => updateField('reportedTco2e', event.target.value)}
                  size="small"
                  type="number"
                  value={draft.reportedTco2e}
                />
              </LabeledField>
            )}
            <LabeledField label={t('climateAction.plan.dialog.resultNote')}>
              <TextField
                disabled={!canMutate}
                fullWidth
                inputProps={{ maxLength: 1000 }}
                minRows={2}
                multiline
                onChange={event => updateField('basisNote', event.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 } }}
                value={draft.basisNote}
              />
              <Typography color="text.secondary" sx={{ fontSize: 11, mt: 0.25, textAlign: 'right' }}>
                {draft.basisNote.length}/1000
              </Typography>
            </LabeledField>
            <LabeledField label={t('climateAction.plan.dialog.resultStatusLabel')} required>
              <TextField
                disabled={!canMutate}
                fullWidth
                onChange={event => updateField('status', event.target.value)}
                select
                size="small"
                SelectProps={{ MenuProps: SCROLLABLE_SELECT_MENU_PROPS }}
                value={draft.status || RESULT_STATUS.DRAFT}
              >
                {RESULT_STATUS_OPTIONS.map(status => (
                  <MenuItem key={status} value={status}>
                    {t(resultStatusLabelKey(status), { defaultValue: status })}
                  </MenuItem>
                ))}
              </TextField>
            </LabeledField>
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t('climateAction.plan.dialog.evidenceColumn')}</Typography>
              <EvidenceUploadList
                evidenceRecords={draft.evidenceFiles || []}
                fiscalYear={fiscalYear}
                onAdd={addEvidence}
                onDownload={downloadEvidence}
                onRemove={removeEvidence}
                readOnly={!canMutate}
                t={t}
              />
            </Stack>
            {saveError && <Alert severity="error" sx={{ borderRadius: 0.5 }}>{saveError}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" disabled={saving} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={saving || !canMutate}
          onClick={handleSave}
          startIcon={saving ? <CircularProgress color="inherit" size={15} /> : undefined}
          variant="contained"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
